import { useCallback } from 'react';
import type { Dayjs } from 'dayjs';
import { useToken } from '@/common/helper';
import {
  formatUserDateTime,
  formatUserDateNumeric,
  type FormatUserDateTimeOptions,
} from '@/common/utils/userDateFormat';

/**
 * Formats timestamps for display using the date_format preference saved on
 * the user's profile (read from the JWT), falling back to MM/DD/YYYY
 */
const useUserDateTimeFormat = () => {
  const { decodedUserToken } = useToken();
  const dateFormat = decodedUserToken()?.date_format;

  const formatDateTime = useCallback(
    (
      value?: string | number | Date | Dayjs | null,
      options?: FormatUserDateTimeOptions
    ): string => formatUserDateTime(value, dateFormat, options),
    [dateFormat]
  );

  // Plain numeric style (e.g. "03/21/2025") for special-case UI like the
  // Home date filter's pill label and calendar inputs.
  const formatDateNumeric = useCallback(
    (value?: string | number | Date | Dayjs | null): string =>
      formatUserDateNumeric(value, dateFormat),
    [dateFormat]
  );

  return { formatDateTime, formatDateNumeric, dateFormat };
};

export default useUserDateTimeFormat;
